import * as A from "fp-ts/Array";
import { flow, identity, pipe } from "fp-ts/function";
import * as O from "fp-ts/Option";
import { not } from "fp-ts/Predicate";
import * as RONEA from "fp-ts/ReadonlyNonEmptyArray";
import * as R from "fp-ts/Record";
import * as S from "fp-ts/string";
import { Decoration_Type, PluginContainerData_Alignment } from "./../../../../../ricos-schema/legacy/wix/rich_content/v1";
import { and, or } from "../../../../fp-utils";
import { createLink, createParagraphNode } from "../../../nodeUtils";
import {
  getAttributes,
  getClassNames,
  getStyle,
  hasChild,
  hasParent,
  hasStyleFor,
  hasStyleRule,
  hasTag,
  isRoot,
  isText,
  isWhitespace,
  oneOf
} from "../core/parse5-utils";
import parse from "../core/parser";
import {
  backgroundStyleToTextHighlight,
  colorStyleToTextColor,
  hToHeading,
  identityRule,
  imgToImage,
  lToList,
  pToParagraph,
  textToText
} from "../core/rules";
import { aToCustomLink } from "./aToCustomLink";
import { iframeToVimeoVideo, iframeToYoutubeVideo } from "./iframeToVideo";
import postprocess from "./postprocess";
import { preprocess } from "./preprocess";
const noEmptyLineText = [
  // @ts-ignore $TSFixMe
  and([isText, not(isWhitespace), not(hasParent(isRoot))]),
  textToText[1]
];
const rootTextToP = [
  // @ts-ignore $TSFixMe
  and([isText, hasParent(isRoot)]),
  (ctx) => (el) => [createParagraphNode(textToText[1](ctx)(el))]
];
const traverseDiv = [hasTag("div"), identityRule[1]];
const traverseSpan = [
  and([
    // @ts-ignore $TSFixMe
    hasTag("span"),
    // @ts-ignore $TSFixMe
    not(hasStyleFor("font-weight")),
    // @ts-ignore $TSFixMe
    not(hasStyleFor("font-style")),
    // @ts-ignore $TSFixMe
    not(hasStyleFor("text-decoration")),
    // @ts-ignore $TSFixMe
    not(hasStyleFor("color")),
    // @ts-ignore $TSFixMe
    not(hasStyleFor("background-color"))
  ]),
  // @ts-ignore $TSFixMe
  (context) => (element) => {
    const processedImageNodes = processImageNode(context, element);
    return processedImageNodes.length > 0 ? processedImageNodes : identityRule[1](context)(element);
  }
];
const fontStyleToItalic = [
  // @ts-ignore $TSFixMe
  or([hasTag("em"), and([hasTag("span"), hasStyleRule({ "font-style": "italic" })])]),
  ({ addDecoration }) => (
    // @ts-ignore $TSFixMe
    (el) => addDecoration(Decoration_Type.ITALIC, {}, el)
  )
];
const fontWeightToBold = [
  // @ts-ignore $TSFixMe
  or([oneOf(["b", "strong"]), and([hasTag("span"), hasStyleRule({ "font-weight": "bold" })])]),
  ({ addDecoration }) => (
    // @ts-ignore $TSFixMe
    (el) => addDecoration(Decoration_Type.BOLD, {}, el)
  )
];
const textDecorationToUnderline = [
  // @ts-ignore $TSFixMe
  or([hasTag("u"), and([hasTag("span"), hasStyleRule({ "text-decoration": "underline" })])]),
  ({ addDecoration }) => (
    // @ts-ignore $TSFixMe
    (el) => addDecoration(Decoration_Type.UNDERLINE, {}, el)
  )
];
const alignmentToTextStyle = (alignment) => ({
  textAlignment: alignment
});
const alignmentToContainerData = (align) => ({
  alignment: align
});
const getAlignment = flow(
  RONEA.filter((c) => ["align-left", "align-right", "align-center"].includes(c)),
  O.map(RONEA.head),
  O.map(S.replace("align-", "")),
  O.fold(() => "", S.toUpperCase)
);
const getAlignmentByClass = flow(getClassNames, getAlignment);
const hasAlignmentClass = flow(getAlignmentByClass, Boolean);
const mergeWithTextStyle = (dataProp, textStyle) => (node) => ({
  ...node,
  [dataProp]: {
    ...node[dataProp],
    textStyle: { ...node[dataProp]?.textStyle, ...textStyle }
  }
});
const mergeWithContainerData = (dataProp, containerData) => (node) => ({
  ...node,
  [dataProp]: {
    ...node[dataProp],
    containerData: {
      ...node[dataProp]?.containerData,
      ...containerData
    }
  }
});
const lineHeightToTextStyle = (lineHeight) => ({ lineHeight });
const getLineHeight = flow(
  getStyle,
  O.chain(R.lookup("line-height")),
  O.fold(() => "", identity)
);
const figureToImage = [
  hasTag("figure"),
  // @ts-ignore $TSFixMe
  (context) => (element) => processImageNode(context, element)
];
const pWitTextToStyledParagraph = [
  // @ts-ignore $TSFixMe
  and([pToParagraph[0], hasChild(not(isWhitespace))]),
  // @ts-ignore $TSFixMe
  (context) => (element) => pipe(
    element,
    pToParagraph[1](context),
    A.map(
      hasAlignmentClass(element) ? mergeWithTextStyle(
        "paragraphData",
        // @ts-ignore $TSFixMe
        pipe(element, getAlignmentByClass, alignmentToTextStyle)
      ) : identity
    ),
    A.map(
      hasStyleFor("line-height")(element) ? mergeWithTextStyle("paragraphData", pipe(element, getLineHeight, lineHeightToTextStyle)) : identity
    )
  )
];
const hToStyledHeading = [
  hToHeading[0],
  // @ts-ignore $TSFixMe
  (context) => (element) => pipe(
    element,
    hToHeading[1](context),
    A.map(
      hasAlignmentClass(element) ? mergeWithTextStyle(
        "headingData",
        // @ts-ignore $TSFixMe
        pipe(element, getAlignmentByClass, alignmentToTextStyle)
      ) : identity
    ),
    A.map(
      hasStyleFor("line-height")(element) ? mergeWithTextStyle("headingData", pipe(element, getLineHeight, lineHeightToTextStyle)) : identity
    )
  )
];
const iframeToAlignedVimeoVideo = [
  iframeToVimeoVideo[0],
  // @ts-ignore $TSFixMe
  (context) => (element) => {
    return pipe(
      element,
      iframeToVimeoVideo[1](context),
      A.map(
        hasAlignmentClass(element) ? mergeWithContainerData(
          "videoData",
          // @ts-ignore $TSFixMe
          pipe(element, getAlignmentByClass, alignmentToContainerData)
        ) : identity
      )
    );
  }
];
const iframeToAlignedYoutubeVideo = [
  iframeToYoutubeVideo[0],
  // @ts-ignore $TSFixMe
  (context) => (element) => {
    return pipe(
      element,
      iframeToYoutubeVideo[1](context),
      A.map(
        hasAlignmentClass(element) ? mergeWithContainerData(
          "videoData",
          // @ts-ignore $TSFixMe
          pipe(element, getAlignmentByClass, alignmentToContainerData)
        ) : identity
      )
    );
  }
];
const imgToAlignedImage = [
  imgToImage[0],
  // @ts-ignore $TSFixMe
  (context) => (element) => {
    return pipe(
      element,
      imgToImage[1](context),
      A.map(
        hasAlignmentClass(element) ? mergeWithContainerData(
          "imageData",
          // @ts-ignore $TSFixMe
          pipe(element, getAlignmentByClass, alignmentToContainerData)
        ) : mergeWithContainerData("imageData", {
          alignment: PluginContainerData_Alignment.CENTER
        })
      )
    );
  }
];
const KNOWN_TAGS = [
  "p",
  "div",
  "span",
  "figure",
  "img",
  "a",
  "iframe",
  "strong",
  "em",
  "u",
  "ol",
  "ul",
  "li",
  "b",
  "i",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6"
];
const unwrapUnknownTag = [
  (node) => {
    return !KNOWN_TAGS.includes(node.nodeName.toLowerCase());
  },
  identityRule[1]
];
const processImageNode = (context, element) => {
  const anchorNode = element.childNodes.find((node) => node.nodeName === "a");
  if (anchorNode) {
    const imageNode = anchorNode.childNodes.find((node) => node.nodeName === "img");
    if (imageNode) {
      const imageNodes = imgToAlignedImage[1](context)(imageNode);
      const linkAttrs = getAttributes(anchorNode);
      const linkData = linkAttrs.href ? {
        link: createLink({ ...linkAttrs, url: linkAttrs.href })
      } : void 0;
      return imageNodes.map((imgNode) => ({
        ...imgNode,
        imageData: {
          ...imgNode.imageData,
          link: linkData?.link
        }
      }));
    }
  }
  const directImageNode = element.childNodes.find((node) => node.nodeName === "img");
  if (directImageNode) {
    return imgToImage[1](context)(directImageNode);
  }
  return [];
};
const fromCKEditorHtml = (s, interceptorFn = ({ result }) => result) => flow(
  () => preprocess(s),
  parse(
    [
      noEmptyLineText,
      rootTextToP,
      pWitTextToStyledParagraph,
      lToList,
      hToStyledHeading,
      aToCustomLink,
      fontWeightToBold,
      fontStyleToItalic,
      colorStyleToTextColor,
      backgroundStyleToTextHighlight,
      textDecorationToUnderline,
      iframeToAlignedYoutubeVideo,
      iframeToAlignedVimeoVideo,
      imgToAlignedImage,
      traverseDiv,
      traverseSpan,
      figureToImage,
      unwrapUnknownTag
    ],
    interceptorFn
  ),
  postprocess
)();
export {
  fromCKEditorHtml
};
//# sourceMappingURL=parser.js.map
